import { PaginationInfo, SuccessResponseList } from './common'
import { User } from './user'

export enum DocumentType {
  TABLE = 'table',
  LIST = 'list',
  FIGURE = 'figure',
}

export enum DocumentStatus {
  NEW = 'new',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  QUERY_RAISED = 'query_raised',
  UNDER_REVISION = 'under_revision',
}

export enum DocumentAction {
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  RAISE_QUERY = 'raise_query',
  UNDER_REVISION = 'under_revision',
}

export enum ReviewerStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

/**
 * Reason for skipping an upload confirmation.
 */
export type SkipReason = (typeof SkipReason)[keyof typeof SkipReason]

export const SkipReason = {
  version_already_exists: 'version_already_exists',
  duplicate_race_condition: 'duplicate_race_condition',
} as const

/**
 * Metadata for a file to be uploaded.
 */
export interface TFLMetadata {
  /** Optional: client-provided document ID to update existing document */
  document_id?: string | null
  /** Type of document */
  document_type?: DocumentType | null
  /** Filename for detection and blob naming */
  filename: string
}

/**
 * Request DTO for batch upload SAS token generation.
 */
export interface RequestBatchUpload {
  /** Target deliverable set ID */
  deliverable_set_id: string
  /** List of files to upload */
  files: TFLMetadata[]
}

/**
 * Upload URL information for a TFL file.
 */
export interface TFLUploadUrl {
  /** Blob name in storage */
  blob_name: string
  /** Pre-signed URL for upload */
  blob_url: string
  /** Actual assigned document ID (may differ from request) */
  document_id: string
  /** Index of the file in the request */
  file_index: number
  /** Whether this is the initial version of a new document */
  is_initial: boolean
  /** Assigned version number */
  version_number: number
}

/**
 * Response DTO for batch SAS token generation.
 */
export interface ResponseBatchSASToken {
  /** SAS token URL for container */
  container_sas_url: string
  /** Expiration time of the SAS token */
  expires_at: string
  /** List of upload URLs */
  upload_urls: TFLUploadUrl[]
}

export interface SuccessResponseListResponseBatchSASToken extends SuccessResponseList<ResponseBatchSASToken> {}

/**
 * Result of an upload attempt.
 */
export interface UploadConfirmation {
  /** Blob URL */
  blob_url: string
  /** Display name for the document */
  display_name?: string | null
  /** Assigned document ID */
  document_id: string
  /** Type of document */
  document_type: DocumentType
  /** Index of the file */
  file_index: number
  /** Actual file size */
  file_size?: number | null
  /** Version is extracted from blob_url */
  version_number?: number | null
}

/**
 * Result of an upload attempt.
 */
/**
 * Request DTO for batch upload confirmation.
 */
export interface RequestBatchUploadConfirm {
  /** List of upload acknowledgement */
  confirmations: UploadConfirmation[]
  /** Target deliverable set ID */
  deliverable_set_id: string
}

/**
 * Result for a failed upload confirmation.
 */
export interface UploadFailureResult {
  /** Error message for the failure */
  error_message: string
  /** Index of the file that failed */
  file_index: number
}

/**
 * Result for a skipped upload confirmation.
 */
export interface UploadSkipResult {
  /** Document ID associated with the skip */
  document_id: string
  /** Index of the file that was skipped */
  file_index: number
  /** Reason for skipping */
  skip_reason: SkipReason
}

/**
 * Response DTO for batch upload confirmation with partial success support.
 */
export interface ResponseBatchUploadConfirm {
  /** Details of failed confirmations */
  failures?: UploadFailureResult[]
  /** Details of skipped confirmations */
  skips?: UploadSkipResult[]
  /** Number of failed confirmations */
  total_failure: number
  /** Number of skipped confirmations (duplicates/re-confirmations) */
  total_skip?: number
  /** Number of successful confirmations */
  total_success: number
}

export interface SuccessResponseListResponseBatchUploadConfirm extends SuccessResponseList<ResponseBatchUploadConfirm> {}

/**
 * Lightweight DTO for document list items.

 Excludes blob URLs to optimize list performance - defer to retrieval endpoint.
 */
export interface ResponseDocumentListItem {
  /** Document creation timestamp */
  created_at: string
  /** Human-readable display name */
  display_name: string
  /** Unique identifier for the document */
  document_id: string
  /** Type of document: table, list, or figure */
  document_type: string
  /** Original filename of the document */
  filename: string
  /** Latest version number (0 if no versions) */
  latest_version_number: number
  /** Number of open comments for the document */
  total_open_comments?: number
  /** Snapshot: Total number of comments for the document */
  snapshot_comment_total: number
  /** Snapshot: Number of open comments */
  snapshot_comment_open: number
  /** Snapshot: Number of resolved comments */
  snapshot_comment_resolved: number
  /** Current document status */
  status: DocumentStatus
  /** Document last update timestamp */
  updated_at: string

  created_by: string
  created_by_user?: User | null
  /** Whether the document is bookmarked by the current user */
  is_bookmarked?: boolean
}

export interface PaginatedResponseResponseDocumentListItem {
  data: ResponseDocumentListItem[]
  pagination: PaginationInfo
}

/**
 * Metadata for the latest document version.
 */
export interface VersionMetadata {
  /** MIME type of the document */
  content_type: string
  /** File size in bytes */
  file_size: number
  /** Upload timestamp (UTC) */
  uploaded_at: string
  /** Version number of the document */
  version_number: number
}

export interface DocumentReviewer {
  id: string
  first_name: string
  last_name: string
  avatar_url: string | null
  status: ReviewerStatus
}

export interface DocumentOwner {
  id: string
  first_name: string
  last_name: string
  email: string
  avatar_url: string | null
}

export interface ResponseDocumentRetrieval {
  /** Document creation timestamp (UTC) */
  created_at: string
  /** User ID who created the document */
  created_by: string
  /** User details for created_by {id, email, first_name, last_name} */
  created_by_user?: User | null
  /** Parent deliverable set ID */
  deliverable_set_id: string
  /** Human-readable document name */
  display_name: string
  /** Unique document identifier */
  document_id: string
  /** Type: table, list, or figure */
  document_type: DocumentType
  /** SAS token expiration time (UTC) */
  expires_at: string
  /** Original filename */
  filename: string
  /** Owner of the deliverable set */
  owner: DocumentOwner
  /** List of reviewers associated with the document */
  reviewers: DocumentReviewer[]
  /** Signed blob URL with read-only SAS token for structured data */
  signed_blob_structured_url?: string | null
  /** Signed blob URL with read-only SAS token */
  signed_blob_url: string
  /** Document review status */
  status: DocumentStatus
  /** Unique version identifier */
  version_id: string
  /** The version number of the document that viewed */
  version_number: number

  // ResponseDocumentListItemDTO
  /** Snapshot: Total number of comments for the document */
  snapshot_comment_total: number
  /** Snapshot: Number of open comments */
  snapshot_comment_open: number
  /** Snapshot: Number of resolved comments */
  snapshot_comment_resolved: number
}

/**
 * Standard metadata included in all responses
 */
export interface ResponseDocumentMetadata {
  /** API version */
  api_version?: string
  /** Audit trail identifier */
  audit_trail_id?: string | null
  /** Compliance assurance level */
  compliance_level?: string
  /** Request correlation ID for tracing */
  correlation_id: string
  /** Request processing time */
  processing_time_ms?: number | null
  /** Remaining requests in current window */
  rate_limit_remaining?: number | null
  /** Seconds until rate limit resets */
  rate_limit_reset?: number | null
  /** Unique request identifier */
  request_id: string
  /** Service version */
  service_version?: string
  /** Response timestamp (ISO format) */
  timestamp?: string
}

export interface SuccessResponseListResponseDocumentRetrieval extends SuccessResponseList<ResponseDocumentRetrieval> {}

export interface ResponseDocumentVersion {
  /** User ID who created the document */
  created_by?: string
  /** User details for created_by {id, email, first_name, last_name} */
  created_by_user?: User | null
  id: string
  version_number: number
}

export interface SuccessResponseListResponseDocumentVersion extends SuccessResponseList<ResponseDocumentVersion> {}

/**
 * DTO for fulltext search request.
 */
export interface RequestFulltextSearchDocument {
  /** Deliverable set ID to scope the search */
  deliverable_set_id: string
  /**
   * Maximum number of items to return (default: 25, max: 100)
   * @minimum 1
   * @maximum 100
   */
  limit?: number
  /**
   * Number of items to skip for pagination (default: 0)
   * @minimum 0
   */
  offset?: number
  /** Search query string */
  q: string
  /** Optional document type to filter the search */
  tfl_type?: DocumentType | null
}

/**
 * DTO representing an AI search result with highlighted snippets.
 */
export interface AISearchResultResponse {
  /** List of text snippets from the document content with highlighted search terms. */
  content_highlights?: string[]
  /** UUID of the deliverable set associated with the document. */
  deliverable_set_id: string
  /** UUID of the document where the result was found. */
  document_id: string
  /** UUID of the specific version of the document. */
  document_version_id: string
  /** Unique identifier of the search result. */
  id: string
  /** Indicates if the document version is active. */
  is_active: boolean
  /** List of keywords extracted from the highlighted snippets. */
  keywords?: string[]
  /** Relevance score of the search result. */
  score: number
  /** Type of the TFL associated with the document. */
  tfl_type: string
  /** Title of the document where the result was found. */
  title: string
  /** Total number of versions available for the document. */
  total_versions: number
  /** Version number of the document version. */
  version_number: number
}

export interface SuccessResponseListAISearchResultResponse extends SuccessResponseList<AISearchResultResponse> {}
